import { lazy, Suspense, useEffect, useState } from "react";
import { BOTTOM_TABS, ROUTES } from "@oshimap/config";
import { TAB_ICONS, ICON_VIEWBOX, ICON_STROKE_WIDTH, ICON_ACCENT_OPACITY, type TabIconName } from "@oshimap/design-tokens";
import { session } from "../lib/api";
import { MapScreen } from "../features/map/MapScreen";
import { HomeScreen } from "../features/home/HomeScreen";
import { ExploreScreen } from "../features/explore/ExploreScreen";
import { SearchScreen } from "../features/explore/SearchScreen";
import { PlaceDetail } from "../features/place/PlaceDetail";
import { PassportScreen } from "../features/passport/PassportScreen";
import { useOAuthReturn } from "../features/auth/useOAuthReturn";
import { registerServiceWorker, applyUpdate, type PwaUpdate } from "../lib/pwa";
import { pruneDrafts } from "../lib/draft";
import { forgetSharedSearches } from "../features/explore/recent-searches";
import { useSessionRecovery } from "../lib/use-session-recovery";
import { MeScreen } from "../features/me/MeScreen";
import { useFavorites } from "../features/favorite/use-favorites";
import { useRoute } from "./use-route";
import { navigate } from "./router";
import { play } from "../lib/motion";
import { Button, EmptyState } from "../components/ui";
import { ScreenBoundary } from "../components/ScreenBoundary";
import "./app.css";

/*
 * 라우트 단위 코드 스플리팅 (NFR-P-03).
 *
 * **하단 탭 다섯은 떼지 않습니다.** 탭을 누를 때마다 스피너가 뜨면 그것은
 * 최적화가 아니라 느려진 것입니다. 지도에서 바로 가는 장소 상세도 남깁니다.
 *
 * 여기 있는 것들은 한 단계 더 들어가야 닿는 화면입니다 — 이벤트 목록·상세,
 * 제보, 방문 기록, 설정, 약관, 그리고 운영 화면 둘. 처음 여는 사람은 이 코드를
 * 받지 않습니다.
 */
const CandidateFeed = lazy(() => import("../features/ingestion/CandidateFeed"));
const ExternalInfo = lazy(() => import("../features/ingestion/ExternalInfo"));
const EventsScreen = lazy(() => import("../features/event/EventsScreen").then((m) => ({ default: m.EventsScreen })));
const EventDetail = lazy(() => import("../features/event/EventDetail").then((m) => ({ default: m.EventDetail })));
const SubmitScreen = lazy(() => import("../features/submit/SubmitScreen").then((m) => ({ default: m.SubmitScreen })));
const VisitNewScreen = lazy(() => import("../features/visit/VisitNewScreen").then((m) => ({ default: m.VisitNewScreen })));
const SettingsScreen = lazy(() => import("../features/settings/SettingsScreen").then((m) => ({ default: m.SettingsScreen })));
const LegalScreen = lazy(() => import("../features/legal/LegalScreen").then((m) => ({ default: m.LegalScreen })));

/**
 * 화면을 받아 오는 동안 보여줄 것.
 *
 * 문구를 화면마다 다르게 적지 않습니다 — 같은 일이 일어나는데 말이 다르면
 * 사람은 다른 일이 일어난 줄 압니다. `role="status"` 라 화면을 못 보는
 * 사람에게도 "지금 뭔가 하고 있다" 가 전해집니다.
 */
function Loading({ what }: { what: string }) {
  return <p className="om-screen om-screen--pad" role="status">{what}을(를) 불러오는 중입니다.</p>;
}

export function App() {
  const route = useRoute();
  const [authed, setAuthed] = useState(false);
  const fav = useFavorites();
  const oauth = useOAuthReturn();
  const [update, setUpdate] = useState<PwaUpdate>(null);

  useEffect(() => {
    registerServiceWorker(setUpdate);
    /* 24시간 지난 초안은 도움이 되지 않습니다. 쌓이면 저장소가 차서 최근 검색까지 막힙니다. */
    pruneDrafts();
    /* 사용자별로 나누기 전에 쓰던 공용 검색 목록. 이제 읽지 않으므로 치웁니다. */
    forgetSharedSearches();
  }, []);

  useEffect(() => {
    void session.restore();
    return session.subscribe((s) => setAuthed(s.status === "authenticated"));
  }, []);

  /* 서버가 잠깐 안 됐을 때 잃은 로그인을, 돌아온 순간에 되찾습니다. */
  useSessionRecovery(session);

  const activeTab =
    route.name === "home" ? "home"
    : route.name === "map" ? "map"
    : route.name === "explore" || route.name === "exploreCategory" || route.name === "search" ? "explore"
    : route.name === "passport" ? "passport"
    : route.name === "me" || route.name === "settings" ? "me"
    : route.name === "event" || route.name === "events" ? "home"
    : route.name === "place" ? "map"
    : null;

  return (
    <div className="om-app">
      {oauth.error ? (
        <p className="om-toast om-toast--err" role="alert" onClick={oauth.dismiss}>{oauth.error}</p>
      ) : null}
      {update ? (
        <div className="om-toast om-toast--new" role="status">
          새 버전이 있어요
          <button type="button" onClick={() => applyUpdate(update)}>새로고침</button>
        </div>
      ) : null}
      <main className="om-app__main">
        {/* 라우트가 바뀌면 다시 시도합니다 — 한 번 터진 화면이 영영 오류로 남지 않게. */}
        <ScreenBoundary key={route.name}>
        {route.name === "candidateFeed" ? <Suspense fallback={<Loading what="수집함" />}><CandidateFeed /></Suspense>
        : route.name === "externalInfo" ? <Suspense fallback={<Loading what="외부 정보" />}><ExternalInfo /></Suspense>
        : route.name === "home" ? <HomeScreen />
        : route.name === "map" ? <MapScreen />
        : route.name === "explore" ? <ExploreScreen />
        : route.name === "exploreCategory" ? <ExploreScreen category={route.params["category"]} />
        : route.name === "search" ? <SearchScreen initialQuery={route.query.get("q") ?? undefined} />
        : route.name === "events" ? <Suspense fallback={<Loading what="이벤트" />}><EventsScreen /></Suspense>
        : route.name === "event" ? <Suspense fallback={<Loading what="이벤트" />}><EventDetail eventId={route.params["eventId"] ?? ""} onBack={() => history.back()} /></Suspense>
        : route.name === "place" ? (
            <PlaceDetail
              placeId={route.params["placeId"] ?? ""}
              isFavorited={fav.isFavorited("PLACE", route.params["placeId"] ?? "")}
              onToggleFavorite={() => fav.toggle("PLACE", route.params["placeId"] ?? "")}
              onBack={() => history.back()}
            />
          )
        : route.name === "passport" ? <PassportScreen section={route.params["section"]} />
        : route.name === "me" ? <MeScreen section={route.params["section"]} reportId={route.params["reportId"]} />
        : route.name === "settings" ? <Suspense fallback={<Loading what="설정" />}><SettingsScreen section={route.params["section"]} /></Suspense>
        : route.name === "submit" ? <Suspense fallback={<Loading what="제보" />}><SubmitScreen placeId={route.params["placeId"]} /></Suspense>
        : route.name === "visitNew" ? <Suspense fallback={<Loading what="방문 기록" />}><VisitNewScreen /></Suspense>
        : route.name === "notFound" ? (
            <div className="om-screen om-screen--pad">
              <h1 className="sr-only">페이지를 찾을 수 없음</h1>
              <EmptyState
                title="페이지를 찾을 수 없어요"
                body={`‘${route.path}’ 경로가 없습니다.`}
                action={<Button variant="primary" size="sm" onClick={() => navigate(ROUTES.home)}>홈으로</Button>}
              />
            </div>
          )
        : route.name === "legal" ? <Suspense fallback={<Loading what="약관" />}><LegalScreen doc={route.params["doc"]} /></Suspense>
        : <NotFound path={route.path} />}
        </ScreenBoundary>
      </main>

      <nav className="om-nav" aria-label="주요 메뉴">
        {BOTTOM_TABS.map((t) => (
          <button
            key={t.key}
            aria-current={activeTab === t.key ? "page" : undefined}
            onClick={(e) => { navigate(t.route); play(e.currentTarget, "mo-pop"); }}
          >
            <TabIcon name={t.key} active={activeTab === t.key} />
            <span className="om-nav__label">{t.label}</span>
          </button>
        ))}
      </nav>

      {/*
        * 라우트 변경을 스크린리더에 알린다.
        * 로그인 상태도 여기로 넣는다 — 문서 끝에 상시 텍스트로 두면 훑을 때마다
        * 읽히기만 하고 **바뀐 순간**은 알려주지 못한다.
        */}
      <div id="om-live" className="sr-only" role="status" aria-live="polite" />
      <div className="sr-only" role="status" aria-live="polite">
        {authed ? "로그인됨" : ""}
      </div>
    </div>
  );
}

/**
 * 하단 탭 아이콘.
 *
 * 모양은 `@oshimap/design-tokens` 한 곳에서 옵니다 — Native 가 같은 좌표를
 * 읽습니다. 전에는 `⌂ ◈ ◎ ▣ ☺` 도형 문자를 Web 과 Native 가 **각자** 들고
 * 있었습니다. 같은 탭이 두 플랫폼에서 다르게 생길 수 있는 상태였습니다.
 *
 * 활성일 때 `accent` 한 조각만 채웁니다. 색만 바꾸면 색각 이상에서 어느 탭에
 * 있는지 알 수 없습니다.
 */
function TabIcon({ name, active }: { name: string; active: boolean }) {
  const shape = TAB_ICONS[name as TabIconName];
  if (!shape) return null;
  return (
    <svg className="om-nav__icon" viewBox={ICON_VIEWBOX} width="24" height="24" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth={ICON_STROKE_WIDTH}
      strokeLinecap="butt" strokeLinejoin="miter">
      <path d={shape.accent} fill={active ? "currentColor" : "none"} fillOpacity={ICON_ACCENT_OPACITY} />
      <path d={shape.accent} />
      {shape.lines.map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

/** 라우터가 모르는 경로. 화면 이름을 그대로 노출하지 않는다. */
function NotFound({ path }: { path: string }) {
  return (
    <div className="om-screen om-screen--pad">
      <h1 className="sr-only">페이지를 찾을 수 없음</h1>
      <EmptyState
        title="페이지를 찾을 수 없어요"
        body={`‘${path}’ 경로가 없습니다.`}
        action={<Button variant="primary" size="sm" onClick={() => navigate(ROUTES.home)}>홈으로</Button>}
      />
    </div>
  );
}
